import {Component, HostListener} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router, UrlTree} from '@angular/router';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from "../_services/auth.service";
import {CommunitiesService} from "../_services/communities.service";
import {Community} from "../_types/communities";
import {LocalStorageService} from "../_services/local-storage.service";
import {ContentService} from "../_services/content.service";
import {mostReadable, TinyColor} from "@ctrl/tinycolor";

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent {
    loginForm: FormGroup;
    requestPasswordForm: FormGroup;
    loading: boolean = false;
    initialLoad: boolean = true;
    passwordType: string = 'password';
    view: 'Login' | 'Create' | 'Forgot' = 'Login'
    companyImg: string | null = null;
    companyName: string = localStorage.getItem('communityName') || 'Explain'
    community: Community | undefined;
    returnUrl: string | undefined;

    constructor(private readonly formBuilder: FormBuilder,
                private readonly communitiesService: CommunitiesService,
                private authService: AuthService,
                private readonly router: Router,
                private readonly snackBar: MatSnackBar,
                private readonly localStorageService: LocalStorageService,
                private contentService: ContentService,
                private readonly route: ActivatedRoute) {
        this.communitiesService.getCommunityByUrl().then((r): void => {
            this.initialLoad = false;
            if (r.login_page_custom_logo) {
                this.companyImg = r.login_page_custom_logo;
            }
            this.companyName = r.name;
            this.community = r;
            if (this.community?.login_card_text_color_hex) {
                document.documentElement.style.setProperty(
                    `--login-text`, this.community?.login_card_text_color_hex
                );
            } else if (this.community?.login_card_background_color_hex) {
                document.documentElement.style.setProperty(
                    `--login-text`, this.getContrastColor(this.community.login_card_background_color_hex)
                );
            } else {
                document.documentElement.style.setProperty(
                    `--login-text`, 'black'
                );
            }
            this.localStorageService.setItem(r, 'lastSelectedCommunity')
            this.contentService.setCurrentDashboardCommunity(r);
        }).catch((err): void => {
            this.openSnackBar('No community found on this domain', 'error')
            this.initialLoad = false;
            this.contentService.setCurrentDashboardCommunity(null);
        });

        this.loginForm = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', Validators.required],
            remember: ['']
        });

        this.requestPasswordForm = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
        });

        let lastSelectedCommunity: Community | null = this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
        localStorage.clear();
        if (lastSelectedCommunity) {
            localStorage.setItem('lastSelectedCommunity', JSON.stringify(lastSelectedCommunity))
        }
        this.route.queryParams.subscribe(params => {
            if (params['returnUrl']) {
                this.validateReturnUrl(decodeURIComponent(params['returnUrl']))
            }
            if (!params['register'] && this.view == 'Create') {
                this.view = 'Login';
            }
        });
    }

    @HostListener('document:keydown.enter', ['$event'])
    onEnterPress(event: KeyboardEvent): void {
        if (this.view === 'Login') {
            this.loginIfValid()
        }
    }

    validateReturnUrl(url: string): void {
        const validPathRegex = /^[a-zA-Z0-9\-._~\/?&=]*$/;
        // Check if the URL matches the valid path pattern
        if (url != undefined && url && validPathRegex.test(url) && !url.includes('404')) {
            try {
                const urlTree: UrlTree = this.router.parseUrl(url);
                // If parseUrl doesn't throw an error, the URL is considered valid
                this.returnUrl = url;
            } catch (e) {
                console.warn('Invalid URL:', url);
                // Handle invalid URL scenario
            }
        }
    }

    showPassword(): void {
        if (this.passwordType == 'password') {
            this.passwordType = 'text';
        } else {
            this.passwordType = 'password';
        }
    }

    login(): void {
        this.loading = true;
        this.authService.login(this.loginForm.value.email, this.loginForm.value.password, this.community?.id)
            .subscribe(
                (data: any): void => {
                    this.localStorageService.removeItem('isApp')
                    if (data.data.user.is_superuser) {
                        this.loginRedirect(data.data)
                    } else {
                        this.communitiesService.getUserCommunities(data.data.token).subscribe(res => {
                            const i: number = res.findIndex((x: any): boolean => x.community.id === this.community?.id);
                            if (i != null && i >= 0) {
                                this.contentService.setCurrentDashboardCommunity(res[i].community)
                                this.loginRedirect(data.data)

                            } else if (window.location.hostname === 'app.explainonline.co.uk') {
                                this.loading = false;
                                this.contentService.setCurrentDashboardCommunity(res[0].community);
                                this.authService.setUser(data.data);
                                // Could redirect users to their subdomain?
                                // window.location.href = res[0].community.front_end_url + 'passthrough?token=' + data.data.token, true;

                                this.router.navigate(['/dashboard']);
                            } else {
                                this.loading = false;
                                this.openSnackBar('Sorry you are not a member of ' + this.community?.name + ' please register', 'error')
                            }
                        })
                    }
                },
                (error: any): void => {
                    if (error.message && error.message.en) {
                        this.openSnackBar(error.message.en, 'error')
                    } else {
                        this.openSnackBar(error, 'error')
                    }
                    this.loading = false;
                });
    }

    loginRedirect(data: any): void {
        // The user has been logged in
        if (this.community?.default_page_is_feed) {
            this.localStorageService.setItem(true, 'homeIsFeed')
        } else {
            this.localStorageService.removeItem('homeIsFeed')
        }
        let url: string = window.location.origin;
        url = url.toLowerCase()
        if (this.community && (url.includes(this.community.alternative_front_end_url?.toLowerCase()) ||
            this.community.alternative_front_end_url?.toLowerCase().includes(url) ||
            this.community.alternative_front_end_url === url)) {
            // if this is the alternative frontend url redirect to the frontend url with the passthrough and returnurl
            let redirectURL: string = this.community.front_end_url
            if (!this.community.front_end_url.endsWith("/")) {
                redirectURL = redirectURL + '/'
            }
            redirectURL = redirectURL + 'passthrough?token=' + data.token
            if (this.returnUrl) {
                redirectURL = redirectURL + '&returnUrl=' + encodeURIComponent(this.returnUrl)
            }
            window.location.href = redirectURL, true;
        } else {
            this.authService.setUser(data);
            // Feed has other params need to check if just useless redirects caused by guards.
            if (this.returnUrl && (this.returnUrl !== '/dashboard' && this.returnUrl !== '/feed' && this.returnUrl !== '/passthrough')) {
                this.router.navigateByUrl(this.returnUrl);
            } else {
                if (this.community?.default_page_is_feed) {
                    this.router.navigate(['/feed']);
                } else {
                    this.router.navigate(['/dashboard']);
                }
            }
        }
    }

    passwordResetRequest(): void {
        this.loading = true;
        this.authService.requestPasswordReset(this.requestPasswordForm.value.email, this.community?.id)
            .subscribe(
                (data: any): void => {
                    this.loading = false;
                    this.openSnackBar(data.message, 'success')
                    this.view = 'Login';
                },
                (error: any): void => {
                    if (error.message && error.message.en) {
                        this.openSnackBar(error.message.en, 'error')
                    } else {
                        this.openSnackBar(error, 'error')
                    }
                    this.loading = false;
                });
    }

    openSnackBar(message: string, type: string): void {
        this.snackBar.open(message, 'Dismiss', {
            duration: 5000,
            panelClass: [type]
        });
    }

    goToRegister() {
        this.view = 'Create'
        this.router.navigate([], {
            queryParams: {'register': true},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    getContrastColor(color?: string): string | null {
        if (color) {
            let fallback: string = new TinyColor(color).isLight() ? 'rgba(black, 0.87)' : 'white';
            let mostReadableColor = mostReadable(color, ["#ffffff", "#000000", "#808080", "#C0C0C0"], {includeFallbackColors: true})?.toHexString()
            if (!mostReadableColor) {
                mostReadableColor = fallback;
            }
            return mostReadableColor
        } else {
            return null
        }
    }

    loginIfValid(): void {
        if (!this.loginForm.invalid && !this.loading) {
            this.login();
        }
    }
}
